import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v3';
import { PageHeading } from '~/components/common/PageHeading';
import { Panel } from '~/components/common/Panel';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { RegionFormFields } from '~/components/upload/region/region-form-fields';
import { uploadRegionUpdateRoute } from '~/paths';
import { useZodForm } from '~/utils/forms';
import { initialRegion, regionSchema } from '~/utils/modules/region';

const CREATE_REGION = graphql(`
  mutation CreateRegion($region: RegionInput!) {
    createRegion(region: $region) {
      id
    }
  }
`);

export default function UploadRegionCreatePage() {
  useBreadcrumb('routes/upload/model.region/create', 'Create Region');
  const navigate = useNavigate();
  const [createRegion, { loading, error }] = useMutation(CREATE_REGION);

  const form = useZodForm({
    schema: regionSchema,
    values: initialRegion(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      const res = await createRegion({ variables: { region: values } });
      const regionId = res.data?.createRegion.id;
      if (!regionId) throw new Error('Error parsing createRegion response');
      navigate(uploadRegionUpdateRoute(regionId));
      toast.success('Region created successfully.');
    } catch (err) {
      console.log('Error creating region:', err);
      toast.error('There was a problem creating the region.');
    }
  });

  return (
    <>
      <PageHeading>Create Region</PageHeading>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Panel>
            <Panel.Heading>
              <Panel.Title>Region Details</Panel.Title>
            </Panel.Heading>
            <Panel.Body className="space-y-4">
              <RegionFormFields />
              <HookFormErrors graphQLError={error} />
            </Panel.Body>

            <Panel.Footer className="text-right">
              <Button type="submit" color="primary" loading={loading}>
                Create
              </Button>
            </Panel.Footer>
          </Panel>
        </form>
      </FormProvider>
    </>
  );
}
